import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import type { Card as StudyCard, Deck, Score } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, FlagDeckButton } from '../../components'
import { showToast } from '../../components/Toast'
import { getDeck, listCards } from '../../lib/decks.js'
import { isOnline, listenToOnlineStatus } from '../../lib/offline.js'
import { createScore, getPersonalBest } from '../../lib/scores.js'
import {
  clearSessionSnapshot,
  loadSessionSnapshot,
  saveSessionSnapshot,
  type SpeedMatchSessionSnapshot,
} from '../../lib/session-storage.js'
import {
  attemptMatch,
  finishSpeedMatch,
  isSpeedMatchComplete,
  restoreSpeedMatch,
  selectTerm,
  startSpeedMatch,
  tickSpeedMatch,
  togglePaused,
  type SpeedMatchState,
} from '../../lib/speed-match.js'
import styles from './SpeedMatchPage.module.css'

interface PageState {
  deck: Deck | null
  cards: StudyCard[]
  loading: boolean
  errorMessage: string | null
}

const INITIAL_PAGE_STATE: PageState = {
  deck: null,
  cards: [],
  loading: true,
  errorMessage: null,
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const remaining = seconds % 60
  return `${minutes}:${String(remaining).padStart(2, '0')}`
}

function snapshotFromState(state: SpeedMatchState): SpeedMatchSessionSnapshot {
  return {
    mode: 'speedMatch',
    deckId: state.deckId,
    classId: state.classId,
    startedAt: state.startedAt.toISOString(),
    selectedTermId: state.selectedTermId,
    matches: Object.fromEntries(state.matches),
    incorrectAttempts: Object.fromEntries(state.incorrectAttempts),
    elapsedSeconds: state.elapsedSeconds,
    paused: state.paused,
    termOrder: state.terms.map((term) => term.cardId),
    definitionOrder: state.definitions.map((definition) => definition.cardId),
  }
}

function bestLabel(score: Score | null) {
  if (!score) return 'No personal best yet'
  return `${Math.round(score.accuracy * 100)}% in ${formatDuration(score.durationSeconds)}`
}

function isTypingTarget(target: EventTarget | null) {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  )
}

export default function SpeedMatchPage(): JSX.Element {
  const { deckId } = useParams()
  const { uid, firebaseUser } = useAuth()
  const navigate = useNavigate()
  const [pageState, setPageState] = useState<PageState>(INITIAL_PAGE_STATE)
  const [matchState, setMatchState] = useState<SpeedMatchState | null>(null)
  const [personalBest, setPersonalBest] = useState<Score | null>(null)
  const [liveMessage, setLiveMessage] = useState('')
  const [savingScore, setSavingScore] = useState(false)
  const [savedScore, setSavedScore] = useState(false)
  const [scoreError, setScoreError] = useState<string | null>(null)
  const [online, setOnline] = useState(isOnline)

  const loadDeck = useCallback(async () => {
    if (!deckId || !uid) return
    setPageState((previous) => ({ ...previous, loading: true, errorMessage: null }))
    setScoreError(null)
    setSavedScore(false)

    try {
      const [deck, cards, best] = await Promise.all([
        getDeck(deckId),
        listCards(deckId),
        getPersonalBest(uid, deckId, 'speedMatch').catch(() => null),
      ])
      setPageState({ deck, cards, loading: false, errorMessage: null })
      setPersonalBest(best)
      if (!deck) {
        setMatchState(null)
        return
      }
      const saved = loadSessionSnapshot(uid, deck.id, 'speedMatch')
      setMatchState(
        saved?.mode === 'speedMatch'
          ? restoreSpeedMatch(deck.id, deck.classId, cards, saved)
          : startSpeedMatch(deck.id, deck.classId, cards),
      )
      setLiveMessage(saved ? 'Resumed your Speed Match session.' : 'Speed Match ready.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load Speed Match.'
      setPageState((previous) => ({ ...previous, loading: false, errorMessage: message }))
      showToast(message, 'error')
    }
  }, [deckId, uid])

  useEffect(() => {
    void loadDeck()
  }, [loadDeck])

  useEffect(() => listenToOnlineStatus(setOnline), [])

  useEffect(() => {
    if (!matchState || matchState.paused || isSpeedMatchComplete(matchState)) return
    const timer = window.setInterval(() => {
      setMatchState((previous) => (previous ? tickSpeedMatch(previous) : previous))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [matchState])

  useEffect(() => {
    if (!uid || !matchState || savingScore || savedScore) return
    saveSessionSnapshot(uid, snapshotFromState(matchState))
  }, [matchState, savedScore, savingScore, uid])

  const matchedDefinitionIds = useMemo(
    () => new Set(matchState ? Array.from(matchState.matches.values()) : []),
    [matchState],
  )
  const complete = matchState ? isSpeedMatchComplete(matchState) : false

  const saveFinishedScore = useCallback(
    async (state: SpeedMatchState) => {
      if (!uid || savingScore || savedScore) return
      setSavingScore(true)
      setScoreError(null)
      try {
        await createScore(finishSpeedMatch(state), uid)
        clearSessionSnapshot(uid, state.deckId, 'speedMatch')
        setSavedScore(true)
        showToast(
          online ? 'Saved your Speed Match score.' : 'Your score will sync when online.',
          'success',
        )
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unable to save your Speed Match score.'
        setScoreError(message)
        showToast(message, 'error')
      } finally {
        setSavingScore(false)
      }
    },
    [online, savedScore, savingScore, uid],
  )

  useEffect(() => {
    if (!matchState || !complete || savedScore) return
    void saveFinishedScore(matchState)
  }, [complete, matchState, saveFinishedScore, savedScore])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (isTypingTarget(event.target) || !matchState) return
      if (event.key === 'Escape' && matchState.selectedTermId) {
        event.preventDefault()
        setMatchState(selectTerm(matchState, null))
        setLiveMessage('Selection cleared.')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [matchState])

  function handleTermPick(termId: string, label: string) {
    if (!matchState || complete) return
    setMatchState(selectTerm(matchState, termId))
    setLiveMessage(`Selected ${label}. Choose a definition.`)
  }

  function handleDefinitionDrop(definitionId: string, label: string) {
    if (!matchState?.selectedTermId || complete) return
    const term = matchState.terms.find((item) => item.cardId === matchState.selectedTermId)
    const result = attemptMatch(matchState, matchState.selectedTermId, definitionId)
    setMatchState(result.state)
    setLiveMessage(
      result.correct
        ? `Matched ${term?.text ?? 'term'} with ${label}.`
        : `${label} was not a match. Try another pair.`,
    )
  }

  function handleRestart() {
    if (!pageState.deck || !uid) return
    clearSessionSnapshot(uid, pageState.deck.id, 'speedMatch')
    setMatchState(startSpeedMatch(pageState.deck.id, pageState.deck.classId, pageState.cards))
    setSavedScore(false)
    setScoreError(null)
    setLiveMessage('Started a new Speed Match session.')
  }

  if (pageState.loading) {
    return (
      <section className={styles.page} aria-labelledby="speedmatch-title">
        <h2 id="speedmatch-title">Speed Match</h2>
        <p role="status" aria-live="polite">
          Loading Speed Match...
        </p>
      </section>
    )
  }

  if (pageState.errorMessage) {
    return (
      <section className={styles.page} aria-labelledby="speedmatch-title">
        <h2 id="speedmatch-title">Speed Match</h2>
        <div className={styles.stateBlock}>
          <p role="alert">{pageState.errorMessage}</p>
          <Button type="button" variant="secondary" onClick={() => void loadDeck()}>
            Retry
          </Button>
        </div>
      </section>
    )
  }

  if (!pageState.deck || !matchState) {
    return (
      <section className={styles.page} aria-labelledby="speedmatch-title">
        <h2 id="speedmatch-title">This deck no longer exists</h2>
        <Link to="/student">Back to student home</Link>
      </section>
    )
  }

  if (pageState.cards.length === 0) {
    return (
      <section className={styles.page} aria-labelledby="speedmatch-title">
        <h2 id="speedmatch-title">{pageState.deck.title || 'Speed Match'}</h2>
        <div className={styles.stateBlock}>
          <p>This deck has no cards yet.</p>
          <Link to="/student">Back to student home</Link>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.page} aria-labelledby="speedmatch-title">
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Speed Match</p>
          <h2 id="speedmatch-title">{pageState.deck.title || 'Untitled deck'}</h2>
          <p className={styles.muted}>Personal best: {bestLabel(personalBest)}</p>
        </div>
        <div className={styles.headerActions}>
          <Link className={styles.modeLink} to={`/student/decks/${pageState.deck.id}/flashcards`}>
            Flashcards
          </Link>
          <FlagDeckButton
            deckId={pageState.deck.id}
            alreadyFlagged={uid ? pageState.deck.flaggedBy.includes(uid) : false}
            getIdToken={() => firebaseUser?.getIdToken() ?? Promise.resolve('')}
          />
          <Button type="button" variant="secondary" onClick={handleRestart}>
            Restart
          </Button>
        </div>
      </div>

      {!online ? (
        <div className={styles.offlineNotice} role="status">
          You are offline. This session is saved on this device and your score will sync when
          reconnected.
        </div>
      ) : null}

      <div className={styles.statusBar}>
        <p>
          <span>{matchState.matches.size}</span> of <span>{matchState.terms.length}</span> matched
        </p>
        <p>
          Time <span>{formatDuration(matchState.elapsedSeconds)}</span>
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setMatchState(togglePaused(matchState))
            setLiveMessage(matchState.paused ? 'Session resumed.' : 'Session paused.')
          }}
        >
          {matchState.paused ? 'Resume' : 'Pause'}
        </Button>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </p>

      {complete ? (
        <div className={styles.completeBlock} role="status">
          <div>
            <h3>Session complete</h3>
            <p>
              {savingScore
                ? 'Saving your result...'
                : savedScore
                  ? 'Your result is saved.'
                  : 'Ready to save your result.'}
            </p>
          </div>
          <Button type="button" variant="primary" onClick={() => navigate('/student')}>
            Back to home
          </Button>
        </div>
      ) : null}

      <div className={styles.matchGrid} aria-label="Speed Match board">
        <section className={styles.column} aria-labelledby="terms-title">
          <h3 id="terms-title">Terms</h3>
          <div className={styles.stack}>
            {matchState.terms.map((term) => {
              const matched = matchState.matches.has(term.cardId)
              const selected = matchState.selectedTermId === term.cardId
              return (
                <button
                  key={term.cardId}
                  type="button"
                  className={[
                    styles.matchItem,
                    selected ? styles.selected : '',
                    matched ? styles.matched : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={matched || matchState.paused || complete}
                  draggable={!matched && !matchState.paused && !complete}
                  onClick={() => handleTermPick(term.cardId, term.text)}
                  onDragStart={(event) => event.dataTransfer.setData('text/plain', term.cardId)}
                  aria-pressed={selected}
                >
                  {term.text || 'Blank term'}
                </button>
              )
            })}
          </div>
        </section>

        <section className={styles.column} aria-labelledby="definitions-title">
          <h3 id="definitions-title">Definitions</h3>
          <div className={styles.stack}>
            {matchState.definitions.map((definition) => {
              const matched = matchedDefinitionIds.has(definition.cardId)
              return (
                <button
                  key={definition.cardId}
                  type="button"
                  className={[styles.matchItem, matched ? styles.matched : '']
                    .filter(Boolean)
                    .join(' ')}
                  disabled={matched || matchState.paused || complete}
                  onClick={() => handleDefinitionDrop(definition.cardId, definition.text)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    const termId = event.dataTransfer.getData('text/plain')
                    if (!termId || !matchState) return
                    const result = attemptMatch(matchState, termId, definition.cardId)
                    setMatchState(result.state)
                    setLiveMessage(
                      result.correct
                        ? `Matched a term with ${definition.text}.`
                        : `${definition.text} was not a match. Try another pair.`,
                    )
                  }}
                >
                  {definition.text || 'Blank definition'}
                </button>
              )
            })}
          </div>
        </section>
      </div>

      {scoreError ? (
        <div className={styles.errorBlock} role="alert">
          <p>{scoreError}</p>
          <Button
            type="button"
            variant="secondary"
            loading={savingScore}
            onClick={() => void saveFinishedScore(matchState)}
          >
            Retry save
          </Button>
        </div>
      ) : null}
    </section>
  )
}
